import Dexie, { type EntityTable } from 'dexie';
import type {
  AppError,
  DownloadMode,
  DownloadState,
  MediaKind,
  SourceIdentity,
} from '../core/types';

/**
 * Small metadata only; file bytes live in OPFS. A full URL is stored only when
 * it has no query string or the user opted in. ARCH-010, F15
 */
export interface SessionRecord {
  id: string;
  createdAt: number;
  updatedAt: number;
  name: string;
  host: string;
  /** Origin + path, never the query. */
  safeUrl: string;
  /** Full URL, present only if safe to keep or the user opted in. */
  storedUrl: string | null;
  extension: string;
  kind: MediaKind;
  /** Container verified from magic bytes, once known. */
  container: string | null;
  contentType: string | null;
  state: DownloadState;
  totalSize: number | null;
  committedBytes: number;
  identity: SourceIdentity | null;
  mode: DownloadMode;
  lastError: AppError | null;
  /** Playback position to resume from. */
  position: number;
}

class OneFlowDb extends Dexie {
  sessions!: EntityTable<SessionRecord, 'id'>;
  constructor() {
    super('oneflow');
    this.version(1).stores({ sessions: 'id, updatedAt, state' });
  }
}

let db: OneFlowDb | null = null;
function open(): OneFlowDb {
  db ??= new OneFlowDb();
  return db;
}

export async function loadSessions(): Promise<SessionRecord[]> {
  try {
    return await open().sessions.orderBy('updatedAt').reverse().toArray();
  } catch {
    return [];
  }
}

export async function saveSession(r: SessionRecord): Promise<void> {
  try {
    await open().sessions.put(r);
  } catch {
    /* Library persistence is best-effort; OPFS manifest remains authoritative for bytes. */
  }
}

export async function patchSession(id: string, patch: Partial<SessionRecord>): Promise<void> {
  try {
    await open().sessions.update(id, { ...patch, updatedAt: Date.now() });
  } catch {
    /* best effort */
  }
}

export async function deleteSessionRecord(id: string): Promise<void> {
  try {
    await open().sessions.delete(id);
  } catch {
    /* best effort */
  }
}
